"use client"

import { useState, useEffect } from "react"
import { useGoogleAuth } from "@/context/GoogleAuthContext"
import { motion } from "framer-motion"
import { FaCalendarAlt, FaClock, FaUsers, FaVideo, FaPlus, FaSpinner, FaGoogle } from "react-icons/fa"
import { MdDescription } from "react-icons/md"
import MeetingFormModal from "./schedule/page"
import { getAccountData } from "@/context/getAccountData";

export default function CalendarPage() {
  const { isGoogleAuthenticated, isLoading, googleUser, signIn, getCalendarEvents } = useGoogleAuth()

  const [events, setEvents] = useState([])
  const [isLoadingEvents, setIsLoadingEvents] = useState(false)
  const [selectedDate, setSelectedDate] = useState(new Date())
  const [currentMonth, setCurrentMonth] = useState(new Date())
  const [calendarDays, setCalendarDays] = useState([])
  const [isModalOpen, setIsModalOpen] = useState(false)

  useEffect(() => {
    console.log("Estado de autenticación:", isGoogleAuthenticated)
    if (isGoogleAuthenticated) {
      fetchEvents()
    }
  }, [isGoogleAuthenticated])

  useEffect(() => {
    generateCalendarDays(currentMonth)
  }, [currentMonth])

  const fetchEvents = async () => {
    try {
      setIsLoadingEvents(true)
      console.log("Obteniendo eventos del calendario...")
      const calendarEvents = await getCalendarEvents()
      console.log("Eventos obtenidos:", calendarEvents)
      setEvents(calendarEvents || [])
    } catch (error) {
      console.error("Error fetching events:", error)
    } finally {
      setIsLoadingEvents(false)
    }
  }

  const [accountData, setAccountData] = useState(null);

  useEffect(() => {

    async function fetchData() {
      try {

        const data = await getAccountData();
        setAccountData(data);
      } catch (error) {
        console.error("Error al obtener los datos de la cuenta:", error);
      }
    }
    fetchData();
  }, []);

  const handleSignIn = () => {
    console.log("Botón de inicio de sesión presionado")
    signIn()
  }

  const generateCalendarDays = (month) => {
    const year = month.getFullYear()
    const monthIndex = month.getMonth()

    // Get first day of month and last day of month
    const firstDay = new Date(year, monthIndex, 1)
    const lastDay = new Date(year, monthIndex + 1, 0)

    // Get the day of the week for the first day (0 = Sunday, 6 = Saturday)
    const firstDayOfWeek = firstDay.getDay()

    // Calculate days from previous month to show
    const daysFromPrevMonth = firstDayOfWeek
    const prevMonthLastDay = new Date(year, monthIndex, 0).getDate()

    const days = []

    // Add days from previous month
    for (let i = daysFromPrevMonth - 1; i >= 0; i--) {
      days.push({
        date: new Date(year, monthIndex - 1, prevMonthLastDay - i),
        isCurrentMonth: false,
      })
    }

    // Add days from current month
    for (let i = 1; i <= lastDay.getDate(); i++) {
      days.push({
        date: new Date(year, monthIndex, i),
        isCurrentMonth: true,
      })
    }

    // Add days from next month to complete the grid (6 rows x 7 days = 42)
    const remainingDays = 42 - days.length
    for (let i = 1; i <= remainingDays; i++) {
      days.push({
        date: new Date(year, monthIndex + 1, i),
        isCurrentMonth: false,
      })
    }

    setCalendarDays(days)
  }

  const goToPreviousMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1))
  }

  const goToNextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1))
  }

  const formatDate = (dateString) => {
    const date = new Date(dateString)
    return date.toLocaleDateString("es-ES", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    })
  }

  const formatTime = (dateString) => {
    const date = new Date(dateString)
    return date.toLocaleTimeString("es-ES", {
      hour: "2-digit",
      minute: "2-digit",
    })
  }

  const isSameDay = (date1, date2) => {
    return (
      date1.getDate() === date2.getDate() &&
      date1.getMonth() === date2.getMonth() &&
      date1.getFullYear() === date2.getFullYear()
    )
  }

  const hasEvents = (date) => {
    return events.some((event) => {
      const eventDate = new Date(event.start.dateTime || event.start.date)
      return isSameDay(eventDate, date)
    })
  }

  // Filter events for the selected date
  const selectedDateEvents = events.filter((event) => {
    const eventDate = new Date(event.start.dateTime || event.start.date)
    return isSameDay(eventDate, selectedDate)
  })

  const weekDays = ["Dom", "Lun", "Mar", "Mié", "Jue", "Vie", "Sáb"]

  const handleOpenModal = () => {
    setIsModalOpen(true)
  }

  const handleCloseModal = () => {
    setIsModalOpen(false)
  }

  const handleMeetingCreated = () => {
    fetchEvents()
    setIsModalOpen(false)
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center">
          <FaSpinner className="animate-spin h-12 w-12 mx-auto text-[var(--navy-blue-light)]" />
          <p className="mt-4 text-lg">Cargando...</p>
        </motion.div>
      </div>
    )
  }

  if (!isGoogleAuthenticated) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen p-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="w-full max-w-md bg-white rounded-lg shadow-lg overflow-hidden"
        >
          <div className="p-6 bg-gradient-to-r from-[var(--navy-blue-light)] to-[var(--navy-blue)] text-white">
            <h2 className="text-2xl font-bold">Acceso a Google Calendar</h2>
            <p className="mt-2 text-blue-100">
              Inicia sesión con tu cuenta de Google para ver tu calendario y crear reuniones
            </p>
          </div>
          <div className="p-6">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={handleSignIn}
              className="w-full flex items-center justify-center gap-2 bg-white border border-gray-300 text-gray-700 rounded-full py-3 px-4 font-medium hover:shadow-md transition-all"
            >
              <FaGoogle className="text-[var(--navy-blue-light)]" />
              Iniciar sesión con Google
            </motion.button>
          </div>
        </motion.div>
      </div>
    )
  }

  return (
    <div className="container mx-auto py-6 px-4">
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
        className="flex flex-col md:flex-row gap-6"
      >
        <div className="md:w-1/3">
          <div className="bg-white rounded-lg shadow-md overflow-hidden">
            <div className="p-4 bg-gradient-to-r from-[var(--navy-blue-light)] to-[var(--navy-blue)] text-white">
              <div className="flex justify-between items-center">
                <h2 className="text-xl font-bold">Mi Calendario</h2>
                {accountData?.username && (
                  <div className="flex items-center gap-2">
                    {accountData.imageUrl && (
                      <img
                        src={accountData.imageUrl || "/placeholder.svg"}
                        alt={accountData.username}
                        className="w-8 h-8 rounded-full border-2 border-white"
                      />
                    )}
                    <span className="text-sm">{accountData.username}</span>
                  </div>
                )}
              </div>
              <div className="flex justify-between items-center mt-4">
                <button onClick={goToPreviousMonth} className="text-white hover:bg-blue-700 rounded-full p-1">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-6 w-6"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
                <h3 className="text-lg font-semibold">
                  {currentMonth.toLocaleDateString("es-ES", { month: "long", year: "numeric" })}
                </h3>
                <button onClick={goToNextMonth} className="text-white hover:bg-blue-700 rounded-full p-1">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-6 w-6"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              </div>
            </div>
            <div className="p-4">
              <div className="grid grid-cols-7 gap-1 mb-2">
                {weekDays.map((day) => (
                  <div key={day} className="text-center text-sm font-medium text-gray-500">
                    {day}
                  </div>
                ))}
              </div>
              <div className="grid grid-cols-7 gap-1">
                {calendarDays.map((day, index) => (
                  <motion.button
                    key={index}
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                    onClick={() => setSelectedDate(day.date)}
                    className={`
                      relative h-10 w-full rounded-full flex items-center justify-center text-sm
                      ${!day.isCurrentMonth ? "text-gray-400" : "text-gray-700"}
                      ${isSameDay(day.date, new Date()) ? "bg-[var(--gold)] font-bold" : ""}
                      ${isSameDay(day.date, selectedDate) ? "bg-[var(--gold)] text-white" : ""}
                    `}
                  >
                    {day.date.getDate()}
                    {hasEvents(day.date) && (
                      <span
                        className={`absolute bottom-1 w-1 h-1 rounded-full ${isSameDay(day.date, selectedDate) ? "bg-white" : "bg-[var(--gold)]"}`}
                      ></span>
                    )}
                  </motion.button>
                ))}
              </div>
              <div className="mt-6">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={handleOpenModal}
                  className="w-full flex items-center justify-center gap-2 bg-[var(--gold)] hover:bg-[var(--gold-dark)] text-white rounded-full py-2 px-4 font-medium transition-colors"
                >
                  <FaPlus className="h-4 w-4" /> Nueva reunión
                </motion.button>
              </div>
            </div>
          </div>
        </div>
        <div className="md:w-2/3">
          <div className="bg-white rounded-lg shadow-md overflow-hidden h-full">
            <div className="p-4 bg-gradient-to-r from-[var(--navy-blue-light)] to-[var(--navy-blue)] text-white">
              <h2 className="text-xl font-bold">Eventos para {formatDate(selectedDate)}</h2>
              <p className="text-sm text-blue-100 mt-1">
                {isLoadingEvents
                  ? "Cargando eventos..."
                  : selectedDateEvents.length === 0
                    ? "No hay eventos programados para este día"
                    : `${selectedDateEvents.length} evento(s) programado(s)`}
              </p>
            </div>
            <div className="p-4 overflow-auto max-h-[600px]">
              {isLoadingEvents ? (
                <div className="flex justify-center py-12">
                  <FaSpinner className="animate-spin h-8 w-8 text-[var(--navy-blue-light)]" />
                </div>
              ) : selectedDateEvents.length > 0 ? (
                <div className="space-y-4">
                  {selectedDateEvents.map((event, index) => (
                    <motion.div
                      key={event.id || index}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.3, delay: index * 0.1 }}
                      className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden"
                    >
                      <div className="p-4 border-l-4 border-[var(--gold)]">
                        <div className="flex justify-between items-start">
                          <h3 className="text-lg font-semibold text-gray-800">{event.summary}</h3>
                          {event.conferenceData && (
                            <motion.a
                              whileHover={{ scale: 1.05 }}
                              whileTap={{ scale: 0.95 }}
                              href={event.conferenceData.entryPoints[0].uri}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex items-center gap-1 bg-[var(--gold)]  text-white px-3 py-1 rounded-full text-sm font-medium hover:bg-[var(--gold-dark)] transition-colors"
                            >
                              <FaVideo className="h-3 w-3" />
                              Unirse
                            </motion.a>
                          )}
                        </div>
                        <div className="mt-3 space-y-2">
                          <div className="flex items-center text-sm text-gray-600">
                            <FaClock className="h-4 w-4 mr-2 text-[var(--navy-blue-light)]" />
                            <span>
                              {formatTime(event.start.dateTime || event.start.date)} -
                              {formatTime(event.end.dateTime || event.end.date)}
                            </span>
                          </div>

                          {event.description && (
                            <div className="flex items-start text-sm text-gray-600">
                              <MdDescription className="h-4 w-4 mr-2 mt-1 text-[var(--navy-blue-light)]" />
                              <div>{event.description}</div>
                            </div>
                          )}

                          {event.attendees && event.attendees.length > 0 && (
                            <div className="flex items-start text-sm text-gray-600">
                              <FaUsers className="h-4 w-4 mr-2 mt-1 text-[var(--navy-blue-light)]" />
                              <div>{event.attendees.map((attendee) => attendee.email).join(", ")}</div>
                            </div>
                          )}
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              ) : (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="flex flex-col items-center justify-center py-12 text-center"
                >
                  <FaCalendarAlt className="h-16 w-16 text-gray-300 mb-4" />
                  <p className="text-gray-500 mb-2">No hay eventos para este día</p>
                  <p className="text-sm text-gray-400">Haz clic en "Nueva reunión" para crear uno</p>
                </motion.div>
              )}
            </div>
          </div>
        </div>
      </motion.div>

      <MeetingFormModal isOpen={isModalOpen} onClose={handleCloseModal} onMeetingCreated={handleMeetingCreated} />
    </div>
  )
}

